import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import CustomerHeader, { CustomerFooter } from "@/components/customer/CustomerHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { http, errMsg, fmtDate } from "@/lib/api";
import { useT } from "@/lib/i18n";

const Score = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1.5" data-testid="review-score">
    {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
      <button type="button" key={n} data-testid={`review-score-${n}`} onClick={() => onChange(n)}
        className={`h-10 w-10 rounded-lg border text-sm font-bold transition-colors ${value === n ? "border-[#5392F9] bg-[#5392F9] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-[#5392F9]"}`}>{n}</button>
    ))}
  </div>
);

export default function ReviewPage() {
  const { token } = useParams();
  const t = useT();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState({ rating: 0, title: "", comment: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    http.get(`/public/review/${token}`).then((r) => { setInfo(r.data); setDone(r.data.submitted); }).catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) return toast.error(t("Pilih nilai 1–10"));
    setBusy(true);
    try { await http.post(`/public/review/${token}`, f); setDone(true); }
    catch (err) { toast.error(t(errMsg(err))); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <CustomerHeader />
      <main className="mx-auto max-w-2xl px-4 py-12">
        {error ? <p data-testid="review-error" className="text-center text-red-600">{t(error)}</p> : !info ? <Skeleton className="h-96 rounded-2xl" /> : done ? (
          <div data-testid="review-thanks" className="fade-up rounded-2xl border border-slate-200 bg-white p-10 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#00A698]" />
            <h1 className="mt-3 font-heading text-2xl font-extrabold">{t("Terima kasih atas review Anda!")}</h1>
            <p className="mt-2 text-sm text-slate-500">{t("Review akan tampil di situs setelah diperiksa oleh tim hotel.")}</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="review-form" className="fade-up space-y-5 rounded-2xl border border-slate-200 bg-white p-8">
            <div>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">{t("Bagaimana menginap Anda?")}</h1>
              <p className="mt-2 text-sm text-slate-500">{info.property_name} · {info.room_name} · {fmtDate(info.check_in, "d MMM")} – {fmtDate(info.check_out, "d MMM yyyy")}</p>
            </div>
            <div><Label>{t("Nilai keseluruhan (1–10)")}</Label><div className="mt-2"><Score value={f.rating} onChange={(rating) => setF({ ...f, rating })} /></div></div>
            <div><Label htmlFor="rtitle">{t("Judul (opsional)")}</Label>
              <Input id="rtitle" data-testid="review-title-input" maxLength={120} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1.5" /></div>
            <div><Label htmlFor="rcomment">{t("Ceritakan pengalaman Anda")}</Label>
              <Textarea id="rcomment" data-testid="review-comment-input" required minLength={3} maxLength={2000} rows={6} value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} className="mt-1.5" /></div>
            <button type="submit" disabled={busy} data-testid="review-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#5392F9] py-3.5 font-heading font-bold text-white transition-colors hover:bg-[#3B7BE6] disabled:bg-slate-300">
              {busy && <Loader2 className="h-5 w-5 animate-spin" />} {t("Kirim review")}
            </button>
          </form>
        )}
      </main>
      <CustomerFooter />
    </div>
  );
}
